/**
 * <frequently-bought>
 *
 * Carousel + variant handling for the "frequently bought together" upsell in the product buy box.
 * Rendered by snippets/product-frequently-bought.liquid. Each direct child is one card (a product
 * form). Variants are picked via an expandable in-flow panel (one row per first-option value with
 * image + name, remaining options as chips). Selecting a chip updates price, image, availability
 * and the hidden variant input of that card only — the form itself posts the selected variant id,
 * so no extra wiring with the main product form is needed.
 */
class FrequentlyBought extends HTMLElement {
  constructor() {
    super();

    this.onClick = this.handleClick.bind(this);
    this.onDocumentClick = this.handleDocumentClick.bind(this);
    this.onKeydown = this.handleKeydown.bind(this);
  }

  get container() {
    return this.closest('.frequently-bought');
  }

  get prevButton() {
    return this.container?.querySelector('[data-prev-button]');
  }

  get nextButton() {
    return this.container?.querySelector('[data-next-button]');
  }

  get moneyFormat() {
    return theme.settings.currencyCodeEnabled
      ? theme.settings.moneyWithCurrencyFormat
      : theme.settings.moneyFormat;
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);

    if (this.classList.contains('flickity') && this.children.length > 1) {
      this.carousel = new theme.Carousel(this, {
        accessibility: false,
        prevNextButtons: false,
        adaptiveHeight: true,
        pageDots: false,
        contain: true,
        cellAlign: 'center',
        rightToLeft: theme.config.rtl,
      }, {
        previous: this.prevButton,
        next: this.nextButton
      });

      this.carousel.load();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);

    if (this.carousel) {
      this.carousel.unload();
    }
  }

  handleClick(event) {
    const toggle = event.target.closest('[data-picker-toggle]');
    if (toggle && this.contains(toggle)) {
      this.togglePanel(toggle.closest('[data-picker]'));
      return;
    }

    const chip = event.target.closest('[data-variant-chip]');
    if (chip && this.contains(chip)) {
      this.selectVariant(chip);
    }
  }

  handleDocumentClick(event) {
    if (this.contains(event.target)) return;
    this.closeAllPanels();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.closeAllPanels();
    }
  }

  togglePanel(picker) {
    if (!picker) return;
    const panel = picker.querySelector('[data-picker-panel]');
    const toggle = picker.querySelector('[data-picker-toggle]');
    if (!panel || !toggle) return;

    const open = panel.hidden;
    this.closeAllPanels();
    panel.hidden = !open;
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    this.carousel?.slider?.resize();
  }

  closeAllPanels() {
    let changed = false;
    this.querySelectorAll('[data-picker-panel]:not([hidden])').forEach((panel) => {
      panel.hidden = true;
      changed = true;
    });
    this.querySelectorAll('[data-picker-toggle][aria-expanded="true"]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', 'false');
    });
    if (changed) this.carousel?.slider?.resize();
  }

  selectVariant(chip) {
    const card = chip.closest('.frequently-bought__card');
    if (!card) return;

    const input = card.querySelector('[data-variant-input]');
    if (input) input.value = chip.dataset.variantId;

    const label = card.querySelector('[data-picker-label]');
    if (label) label.textContent = chip.dataset.title;

    card.querySelectorAll('[data-variant-chip].is-selected').forEach((el) => el.classList.remove('is-selected'));
    chip.classList.add('is-selected');

    this.updatePrice(card, chip);
    this.updateImage(card, chip);
    this.updateButton(card, chip);

    this.togglePanel(chip.closest('[data-picker]'));
  }

  updatePrice(card, option) {
    const price = parseInt(option.dataset.price, 10);
    const compareAtPrice = parseInt(option.dataset.compareAtPrice, 10) || 0;

    const regularElement = card.querySelector('.price__regular');
    if (regularElement) {
      regularElement.innerHTML = theme.Currency.formatMoney(price, this.moneyFormat);
    }

    const onSale = compareAtPrice > price;
    card.querySelector('[data-price-wrapper]')?.classList.toggle('price--on-sale', onSale);

    const saleElement = card.querySelector('.price__sale');
    if (saleElement) {
      saleElement.innerHTML = onSale ? theme.Currency.formatMoney(compareAtPrice, this.moneyFormat) : '';
      saleElement.hidden = !onSale;
    }
  }

  updateImage(card, option) {
    const image = card.querySelector('.frequently-bought__media img');
    if (!image || !option.dataset.imgSrc) return;

    image.src = option.dataset.imgSrc;
    if (option.dataset.imgSrcset) image.srcset = option.dataset.imgSrcset;
  }

  updateButton(card, option) {
    const button = card.querySelector('[data-add-button]');
    if (!button) return;

    const available = option.dataset.available === 'true';
    const label = button.querySelector('[data-sold-out]');

    button.disabled = !available;
    if (label) {
      label.textContent = available
        ? label.getAttribute('data-add-label') || label.textContent
        : label.getAttribute('data-sold-out');
    }
  }
}

if (!customElements.get('frequently-bought')) {
  customElements.define('frequently-bought', FrequentlyBought);
}
